import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import useAgentStore from "./useAgentStore";
import useTaskQueueStore from "./useTaskQueueStore";
import useChatStreamStore from "./useChatStreamStore";
import ConversationWorkspace from "./conversation/ConversationWorkspace";
import { buildAgentSessionCapabilities } from "./conversation/conversationAdapters";
import NewChatComposer from "./NewChatComposer";
import PendingChatView from "./PendingChatView";
import ExpandedSidebar from "./ExpandedSidebar";
import usePublicSessions from "./usePublicSessions";
import useResizePanels from "./useResizePanels";
import useSessionActions from "./useSessionActions";
import useTaskQueuePanel from "./useTaskQueuePanel";

import {
  PanelRoot,
  ChatArea,
  normalizeChatUiPolicy,
} from "./agentSessionsPanelUtils";
import { useStatusPalette } from "../../utils/statusColors";

function AgentSessionsPanel({
  agentId,
  agentName,
  agentDescription,
  agentInboxAddress,
  chatUiPolicy,
  initialSessionId,
  requestedSessionId,
  onSessionSelect,
  onConfigOpen,
  mode = "private",
  publicShareToken = "",
  publicIdentityPayload = {},
  initialPublicSessionToken = "",
  onPublicSessionTokenChange,
  composerDisabled = false,
  debugModeEnabled = false,
  showSessionSidebar = true,
  newChatRequestNonce = 0,
  selectSessionRequest = null,
  taskQueueOpenSignal = 0,
  taskQueueToggleSignal = 0,
  onTaskQueueToggleStateChange,
  splitChatFixedWidth = null,
  inlineTaskQueueToggle = true,
  headerActions = null,
  agentModel,
  chatContext,
  welcomePanelContent = null,
  examplePrompts = [],
  initialComposerDraft = null,
  autoStartInitialPrompt = false,
  onTemplateLaunchConsumed = null,
  hideWelcomeComposerInput = false,
  publicUserDisplayName = "",
  sidebarCollapsed: sidebarCollapsedProp,
  onSidebarCollapsedChange,
}) {
  const _sp = useStatusPalette();
  const isPublicMode = mode === "public";
  const isNarrowQueueMode = false; // narrow detection moved into AgentConversationView
  const panelRootRef = useRef(null);

  const storeSessions = useAgentStore((state) => state.sessions);
  const storeSessionsLoading = useAgentStore((state) => state.sessionsLoading);
  const storeSessionsHasMore = useAgentStore((state) => state.sessionsHasMore);
  const storeSessionsLoadingMore = useAgentStore(
    (state) => state.sessionsLoadingMore
  );
  const storeSessionDetail = useAgentStore((state) => state.sessionDetail);
  const storeSessionDetailLoading = useAgentStore(
    (state) => state.sessionDetailLoading
  );
  const storeTaskQueueEventsBySessionId = useTaskQueueStore(
    (state) => state.taskQueueEventsBySessionId
  );
  const storeSessionStreamStateBySessionId = useChatStreamStore(
    (state) => state.sessionStreamStateBySessionId
  );

  const effectiveChatUiPolicy = {
    ...normalizeChatUiPolicy(chatUiPolicy),
    ...(publicUserDisplayName ? { publicUserDisplayName } : {}),
  };
  const taskQueuePlannerUiEnabled =
    effectiveChatUiPolicy.showTaskQueuePlannerInChat === true;
  const showSidebar = showSessionSidebar;
  const canCollapseSidebar =
    showSidebar && typeof onSidebarCollapsedChange === "function";
  const isSidebarCollapsed =
    canCollapseSidebar && sidebarCollapsedProp === true;

  const [sidebarTab, setSidebarTab] = useState("sessions");

  // --- Hooks ---

  const setSelectedSessionIdRef = useRef(null);
  const handleSessionInvalidated = useCallback(() => {
    if (setSelectedSessionIdRef.current) {
      setSelectedSessionIdRef.current(null);
    }
  }, []);
  const handleSetSelectedSessionId = useCallback((id) => {
    if (setSelectedSessionIdRef.current) {
      setSelectedSessionIdRef.current(id);
    }
  }, []);

  const pub = usePublicSessions({
    isPublicMode,
    publicShareToken,
    publicIdentityPayload,
    showSidebar,
    initialPublicSessionToken,
    onSessionSelect,
    onPublicSessionTokenChange,
    onSessionInvalidated: handleSessionInvalidated,
    onSetSelectedSessionId: handleSetSelectedSessionId,
  });

  const sa = useSessionActions({
    agentId,
    agentName,
    isPublicMode,
    composerDisabled,
    publicShareToken,
    publicIdentityPayload,
    publicUserDisplayName,
    initialSessionId,
    requestedSessionId,
    onSessionSelect,
    showSidebar,
    chatContext,
    pub,
  });

  // Wire the ref so pub's callbacks can update selectedSessionId
  setSelectedSessionIdRef.current = sa.setSelectedSessionId;

  const tq = useTaskQueuePanel({
    selectedSessionId: sa.selectedSessionId,
    isPublicMode,
    selectedPublicSessionToken: pub.selectedPublicSessionToken,
    publicIdentityPayload,
    taskQueuePlannerUiEnabled,
    taskQueueOpenSignal,
    isNarrowQueueMode,
    resolvePrivateClientId: sa.resolvePrivateClientId,
    storeTaskQueueEventsBySessionId,
    storeSessionStreamStateBySessionId,
  });

  const resize = useResizePanels({
    panelRootRef,
    showSidebar,
    taskQueuePanelWidth: tq.taskQueuePanelWidth,
    setTaskQueuePanelWidth: tq.setTaskQueuePanelWidth,
  });

  // --- Sidebar collapse ---

  const handleToggleSidebarCollapse = useCallback(() => {
    if (typeof onSidebarCollapsedChange === "function") {
      onSidebarCollapsedChange(!sidebarCollapsedProp);
    }
  }, [onSidebarCollapsedChange, sidebarCollapsedProp]);

  const sidebarRestoreButton = isSidebarCollapsed ? (
    <Tooltip title="Show sessions" placement="right" arrow>
      <IconButton
        size="small"
        onClick={handleToggleSidebarCollapse}
        aria-label="Show sessions sidebar"
        sx={{
          width: 32,
          height: 32,
          borderRadius: "10px",
          border: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
          color: "text.secondary",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          "&:hover": {
            backgroundColor: "action.hover",
            color: "text.primary",
          },
        }}
      >
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  const handleOpenAgentSettings = useCallback(
    (targetAgentId = null) => {
      if (!onConfigOpen) return;
      onConfigOpen(targetAgentId || agentId);
    },
    [agentId, onConfigOpen]
  );

  // --- Effects ---

  // Forward nonce-driven requests to session actions
  useEffect(() => {
    sa.handleNewChatRequestNonce(newChatRequestNonce);
  }, [newChatRequestNonce, sa]);

  useEffect(() => {
    sa.handleSelectSessionRequest(selectSessionRequest);
  }, [selectSessionRequest, sa]);

  // Reset sidebar tab if scheduled tab not available
  useEffect(() => {
    if (
      (!effectiveChatUiPolicy.showSchedulingActivityInChat || isPublicMode) &&
      sidebarTab === "scheduled"
    ) {
      setSidebarTab("sessions");
    }
  }, [
    effectiveChatUiPolicy.showSchedulingActivityInChat,
    isPublicMode,
    sidebarTab,
  ]);

  // Status-change forwarding
  useEffect(() => {
    sa.handleStatusChange(storeSessionDetail?.status);
  }, [storeSessionDetail?.status, sa]);

  // Re-clamp widths on detail changes
  useEffect(() => {
    resize.reclamp();
  }, [
    storeSessionDetail,
    pub.publicSessionDetail,
    sa.selectedSessionId,
    resize,
  ]);

  // --- Derived values ---

  const sessions = isPublicMode ? pub.publicSessions : storeSessions;
  const sessionsLoading = isPublicMode
    ? pub.publicSessionsLoading
    : storeSessionsLoading;
  const sessionsHasMore = isPublicMode ? false : storeSessionsHasMore;
  const sessionsLoadingMore = isPublicMode ? false : storeSessionsLoadingMore;

  const handleSessionsListScroll = useCallback(
    (e) => {
      if (!sessionsHasMore || sessionsLoadingMore || !agentId) return;
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
        useAgentStore.getState().fetchMoreSessions(agentId);
      }
    },
    [sessionsHasMore, sessionsLoadingMore, agentId]
  );

  const sessionDetail = isPublicMode
    ? storeSessionDetail || pub.publicSessionDetail
    : storeSessionDetail;
  const sessionDetailLoading = isPublicMode
    ? storeSessionDetailLoading || pub.publicSessionDetailLoading
    : storeSessionDetailLoading;
  const conversationCapabilities = buildAgentSessionCapabilities(
    effectiveChatUiPolicy,
    isPublicMode
  );
  const sidebarTabs =
    effectiveChatUiPolicy.showSchedulingActivityInChat && !isPublicMode
      ? [
          { key: "sessions", label: "Sessions" },
          { key: "scheduled", label: "Scheduled" },
        ]
      : [{ key: "sessions", label: "Sessions" }];
  const hasActiveConversation =
    Boolean(sa.selectedSessionId) &&
    sa.selectedSessionId !== "__pending__" &&
    Boolean(sessionDetail);
  const showTaskQueueToggle =
    taskQueuePlannerUiEnabled &&
    hasActiveConversation &&
    (!isPublicMode || Boolean(pub.selectedPublicSessionToken));
  const showFloatingSidebarRestore =
    isSidebarCollapsed &&
    (sa.isNewChat ||
      sa.selectedSessionId === "__pending__" ||
      !sa.selectedSessionId ||
      !sessionDetail);

  useEffect(() => {
    if (typeof onTaskQueueToggleStateChange !== "function") return;
    onTaskQueueToggleStateChange({
      visible: showTaskQueueToggle,
      open: showTaskQueueToggle && tq.taskQueuePanelOpen,
    });
  }, [
    onTaskQueueToggleStateChange,
    showTaskQueueToggle,
    tq.taskQueuePanelOpen,
  ]);

  const lastHandledTaskQueueToggleSignalRef = useRef(taskQueueToggleSignal);
  useEffect(() => {
    if (taskQueueToggleSignal === lastHandledTaskQueueToggleSignalRef.current)
      return;
    lastHandledTaskQueueToggleSignalRef.current = taskQueueToggleSignal;
    if (!showTaskQueueToggle) return;
    if (tq.taskQueuePanelOpen) {
      tq.handleCloseTaskQueuePanel();
    } else {
      tq.handleOpenTaskQueuePanel();
    }
  }, [
    taskQueueToggleSignal,
    showTaskQueueToggle,
    tq.taskQueuePanelOpen,
    tq.handleCloseTaskQueuePanel,
    tq.handleOpenTaskQueuePanel,
  ]);

  const taskQueuePanelConnectionState = tq.taskQueueRevisionSyncing
    ? "reconciling"
    : tq.taskQueueConnectionState;
  const taskQueuePanelProps = {
    snapshot: tq.taskQueueSnapshot,
    loading: tq.taskQueueLoading,
    applying: tq.taskQueueApplying,
    error: tq.taskQueueError,
    connectionState: taskQueuePanelConnectionState,
    queueHistory: tq.taskQueueHistory,
    selectedQueueId: tq.selectedQueueId,
    onSelectQueue: tq.handleSelectQueue,
    onRefresh: () => tq.loadTaskQueueSnapshot({ silent: false }),
    onAction: tq.handleApplyTaskQueueAction,
    onExpandedModeChange: tq.handleTaskQueueExpandedModeChange,
  };

  // --- Shared NewChatComposer props ---

  const newChatComposerProps = {
    onSend: sa.handleStartChat,
    disabled: sa.startingChat || (isPublicMode && composerDisabled),
    agentName,
    agentDescription,
    agentInboxAddress: isPublicMode ? "" : agentInboxAddress,
    agentId,
    agentModel,
    onConfigOpen: isPublicMode ? undefined : handleOpenAgentSettings,
    showSessionOperationalMetaInChat:
      effectiveChatUiPolicy.showSessionOperationalMetaInChat,
    welcomePanelContent,
    examplePrompts,
    initialComposerDraft,
    autoSendInitialDraft: autoStartInitialPrompt,
    onTemplateLaunchConsumed,
    hideComposerInput: hideWelcomeComposerInput,
  };

  // --- Render ---

  return (
    <PanelRoot ref={panelRootRef}>
      {/* Sidebar */}
      {showSidebar && !isSidebarCollapsed && (
        <ExpandedSidebar
          agentId={agentId}
          sidebarWidth={resize.sidebarWidth}
          sidebarTab={sidebarTab}
          sidebarTabs={sidebarTabs}
          onSidebarTabChange={setSidebarTab}
          sessions={sessions}
          sessionsLoading={sessionsLoading}
          sessionsLoadingMore={sessionsLoadingMore}
          selectedSessionId={sa.selectedSessionId}
          isNewChat={sa.isNewChat}
          onSelectSession={sa.handleSelectSession}
          onNewChat={sa.handleNewChat}
          onCollapseSidebar={handleToggleSidebarCollapse}
          showCollapse={canCollapseSidebar}
          onSessionsListScroll={handleSessionsListScroll}
          onSidebarResizeStart={resize.handleSidebarResizeStart}
          showWorkflowProvenanceInChat={
            effectiveChatUiPolicy.showWorkflowProvenanceInChat
          }
        />
      )}

      {/* Main chat area */}
      <ChatArea sx={{ position: "relative" }}>
        {showFloatingSidebarRestore && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 12,
            }}
          >
            {sidebarRestoreButton}
          </Box>
        )}
        {isPublicMode && pub.publicError && (
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: `1px solid ${_sp.red.border}`,
              backgroundColor: _sp.red.bg,
            }}
          >
            <Typography sx={{ fontSize: "12px", color: _sp.red.color }}>
              {pub.publicError}
            </Typography>
          </Box>
        )}

        {sa.isNewChat ? (
          <NewChatComposer {...newChatComposerProps} />
        ) : sa.selectedSessionId === "__pending__" ? (
          <PendingChatView
            agentName={agentName}
            agentId={agentId}
            onOpenAgentSettings={
              isPublicMode ? undefined : handleOpenAgentSettings
            }
            pendingMessage={
              isPublicMode ? pub.publicPendingUserMessage : undefined
            }
            forceUserNameAsYou={effectiveChatUiPolicy.forceUserNameAsYou}
            publicUserDisplayName={publicUserDisplayName}
          />
        ) : !sa.selectedSessionId ? (
          <NewChatComposer {...newChatComposerProps} />
        ) : sessionDetailLoading && !sessionDetail ? (
          <Box sx={{ p: 3 }}>
            <Skeleton variant="text" width={200} height={22} sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Skeleton
                variant="rectangular"
                height={48}
                sx={{ borderRadius: "12px", maxWidth: "60%" }}
              />
              <Skeleton
                variant="rectangular"
                height={36}
                sx={{
                  borderRadius: "12px",
                  maxWidth: "45%",
                  alignSelf: "flex-end",
                }}
              />
              <Skeleton
                variant="rectangular"
                height={64}
                sx={{ borderRadius: "12px", maxWidth: "55%" }}
              />
            </Box>
          </Box>
        ) : sessionDetail ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              overflow: "hidden",
            }}
          >
            <ConversationWorkspace
              session={sessionDetail}
              agentId={agentId}
              agentName={agentName}
              agentModel={agentModel}
              agentInboxAddress={isPublicMode ? "" : agentInboxAddress}
              capabilities={conversationCapabilities}
              chatUiPolicy={effectiveChatUiPolicy}
              headerActions={headerActions}
              leadingActions={sidebarRestoreButton}
              onOpenAgentSettings={
                isPublicMode ? undefined : handleOpenAgentSettings
              }
              onSendMessage={
                isPublicMode ? sa.handleSendPublicMessage : undefined
              }
              allowScheduling={!isPublicMode}
              allowStop={!isPublicMode}
              composerDisabled={Boolean(isPublicMode && composerDisabled)}
              debugModeEnabled={debugModeEnabled && !isPublicMode}
              splitChatFixedWidth={splitChatFixedWidth}
              showTaskQueueToggle={showTaskQueueToggle}
              taskQueuePanelOpen={tq.taskQueuePanelOpen}
              taskQueuePanelProps={taskQueuePanelProps}
              taskQueuePanelWidth={tq.taskQueuePanelWidth}
              taskQueueExpandedMode={tq.taskQueueExpandedMode}
              onOpenTaskQueuePanel={tq.handleOpenTaskQueuePanel}
              onCloseTaskQueuePanel={tq.handleCloseTaskQueuePanel}
              onTaskQueueResizeStart={resize.handleTaskQueueResizeStart}
            />
          </Box>
        ) : null}
      </ChatArea>
    </PanelRoot>
  );
}

export default AgentSessionsPanel;
